import { useState } from 'react';
import { useRouter } from '../../router.tsx';
import { runWrite } from '../../ui/changes.ts';
import { useToast } from '../../ui/toasts.tsx';
import type { BulkResult } from './types.ts';
import { MarketPicker } from './ui.tsx';

function summarize(result: BulkResult): string {
  const failed = result.results.filter((r) => !r.ok);
  return `${result.done} of ${result.results.length} done${failed.length ? ` · ${failed[0]!.detail}${failed.length > 1 ? ` (+${failed.length - 1})` : ''}` : ''}`;
}

/** Shown above the coupons list when rows are selected. */
export function CouponBulkBar({ selected, sites, onDone }: { selected: string[]; sites: { ref: string; name: string }[]; onDone: () => void }) {
  const toast = useToast();
  const [offerTo, setOfferTo] = useState<string[]>([]);
  if (selected.length === 0) return null;
  const run = async (action: 'expire' | 'archive' | 'offer') => {
    const result = await runWrite<BulkResult>(toast, 'admin.coupons.bulk', { action, couponRefs: selected, propertyRefs: action === 'offer' ? offerTo : [] }, (r) => `${action === 'expire' ? 'Expired' : action === 'archive' ? 'Archived' : 'Offered'}: ${summarize(r)}`);
    if (result) onDone();
  };
  return (
    <div className="bulk-bar" role="region" aria-label="Selected coupons">
      <strong>{selected.length} selected</strong>
      <button type="button" className="btn btn-small" onClick={() => run('expire')} title="End date = now, on every site">
        Expire now
      </button>
      <button type="button" className="btn btn-small" onClick={() => run('archive')} title="Archive on each of your sites">
        Archive
      </button>
      <span className="bulk-sep" aria-hidden="true" />
      <span className="muted">Offer to</span>
      {sites.map((site) => (
        <label key={site.ref} className="inline-check">
          <input type="checkbox" checked={offerTo.includes(site.ref)} onChange={(e) => setOfferTo(e.target.checked ? [...offerTo, site.ref] : offerTo.filter((r) => r !== site.ref))} />
          {site.name}
        </label>
      ))}
      <button type="button" className="btn btn-small" disabled={!offerTo.length} onClick={() => run('offer')}>
        Offer
      </button>
      <button type="button" className="btn btn-small btn-ghost" onClick={onDone}>
        Clear
      </button>
    </div>
  );
}

/** Copy to another market and delete, at the bottom of the coupon drawer. */
export function CouponMoreActions({ couponRef }: { couponRef: string }) {
  const toast = useToast();
  const { setQuery } = useRouter();
  const [target, setTarget] = useState('');
  const [armed, setArmed] = useState(false);

  const duplicate = async () => {
    const result = await runWrite<{ ref: string; warnings: string[] }>(toast, 'admin.coupons.duplicate', { ref: couponRef, market: target }, (r) => `Copied to ${target}${r.warnings.length ? ` · ${r.warnings.join('; ')}` : ''}`);
    if (result) setQuery({ coupon: result.ref });
  };
  const remove = async () => {
    const ok = await runWrite(toast, 'admin.coupons.delete', { ref: couponRef, confirm: true }, 'Coupon deleted');
    if (ok) setQuery({ coupon: null });
    setArmed(false);
  };

  return (
    <details className="more-actions">
      <summary>More: copy to another market, delete</summary>
      <div className="toolbar">
        <MarketPicker value={target} onChange={setTarget} label="Copy to" />
        <button type="button" className="btn btn-small" disabled={!target} onClick={duplicate}>
          Copy
        </button>
        <span className="bulk-sep" aria-hidden="true" />
        {armed ? (
          <>
            <span className="text-warn">Delete for good?</span>
            <button type="button" className="btn btn-small btn-danger-ghost" onClick={remove}>
              Yes, delete
            </button>
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setArmed(false)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-small btn-ghost btn-danger-ghost" onClick={() => setArmed(true)} title="Only coupons that were never approved; others are archived">
            Delete
          </button>
        )}
      </div>
    </details>
  );
}
